import React, { useEffect, useRef, useState } from 'react';
import { observer } from "mobx-react-lite";
import { autorun } from "mobx";
import { TIMELINE_RULER_TICK_WIDTH_PX } from "../../config/config";
// import { ApplicationStore } from "../../store/ApplicationStore";
import { CompositionStateStore } from "../../store/CompositionStateStore";
import { TimelineStore } from "../../store/TimelineStore";
import { TIMELINE_CONTROLS_ELEMENT_ID, TIMELINE_ELEMENT_ID, TIMELINE_TRACKS_CONTAINER_ID } from "./config/constants";
import { CompositionService } from "../../services/CompositionService";
import { convertUnitsToTime } from "../../utils/dom/convertUnitsToTime";
import './Timeline.styles.scss';
// import TrackDivider from "./components/TrackDivider/TrackDivider";
import Controls from "./components/Controls/Controls";
// import EmptyTimelineButton from "./components/EmptyTimelineButton/EmptyTimelineButton";
import CanvasTimeRuler from "./components/CanvasTimeRuler/CanvasTimeRuler";
import Playhead from "./components/Playhead/Playhead";
import TrackDivider from "./components/TrackDivider/TrackDivider";
import { Track } from "./components/Track/Track";

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5.0;

const ZOOM_SENSITIVITY = 0.0025; // tweak this

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

// @ts-ignore
const Timeline = observer(({timelineHidden, toggleTimelineHide}) => {
    const [playheadX, setPlayheadX] = useState(0);
    const [orderedLayerIds, setOrderedLayerIds] = useState<string[]>(() => CompositionStateStore.orderedLayerIds ?? []);
    // const [librettoClipLayers, setLibrettoClipLayers] = useState(CompositionStore.layerToLibrettoClips);
    const [zoom, setZoom] = useState(TimelineStore.zoom);
    const [duration, setDuration] = useState(CompositionStateStore.duration);

    const timelineTracksRef = useRef(null);
    const timelineTracksResizeObserver = useRef(null);

    useEffect(() => {
        const disposeAutorun = autorun(() => {
            // This will re-run whenever any observable accessed inside changes
            // setLibrettoClipLayers(CompositionStore.layerToLibrettoClips);
            setOrderedLayerIds(CompositionStateStore.orderedLayerIds);
            setDuration(CompositionStateStore.duration);
            setZoom(TimelineStore.zoom);
        });

        return () => {
            disposeAutorun();
            removeScrollSync();
            // @ts-ignore
            timelineTracksResizeObserver.current?.disconnect();
        };
    }, []);

    useEffect(() => {
        TimelineStore.setWidth(CompositionStateStore.duration * TIMELINE_RULER_TICK_WIDTH_PX);
    }, [duration]);

    useEffect(() => {
        addScrollSync();

        // @ts-ignore
        timelineTracksResizeObserver.current = new ResizeObserver(handleTimelineTracksResize);
        if (timelineTracksRef.current) {
            // @ts-ignore
            timelineTracksResizeObserver.current.observe(timelineTracksRef.current);
        }

        return () => {
            removeScrollSync();
            // @ts-ignore
            timelineTracksResizeObserver.current?.disconnect();
        };
    }, []);

    const handleScroll = () => {
        if (!timelineTracksRef.current) return;

        // @ts-ignore
        setPlayheadX(-timelineTracksRef.current.scrollLeft);

        const event = new CustomEvent('tracksScrollLeft', {
            bubbles: true,
            // @ts-ignore
            detail: timelineTracksRef.current.scrollLeft
        });
        // @ts-ignore
        timelineTracksRef.current.dispatchEvent(event);
    };

    const addScrollSync = () => {
        // @ts-ignore
        timelineTracksRef.current?.addEventListener("scroll", handleScroll);
    };

    const removeScrollSync = () => {
        // @ts-ignore
        timelineTracksRef.current?.removeEventListener("scroll", handleScroll);
    };

    // @ts-ignore
    const handleTimelineTracksResize = (entries) => {
        const width = entries[0]?.contentRect?.width;
        if (width) {
            TimelineStore.setTracksContainerWidth(width);
        }
    };

    // @ts-ignore
    // const handleCreateLayerMedia = async (event, mediaId, index) => {
    //     const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
    //     const layer = CompositionService.createLayer(index);
    //     const clip = await CompositionService.addMediaToLayer(layer.id, mediaId, startTime);
    //     if (clip) {
    //         // let librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
    //         // if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
    //         //     librettoClipId = GenerateLibrettoClipId();
    //         //     clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
    //         // }
    //         //ApplicationStore.setSelectedLibrettoClipId(librettoClipId);
    //     }
    // };

    // @ts-ignore
    // const handleCreateLayerText = async (event, title, index) => {
    //     const layer = CompositionService.createLayer(index);
    //     const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
    //     const clip = await CompositionService.createLottieClip({
    //         dataUrl: title.dataUrl,
    //         propertiesUrl: title.propertiesUrl,
    //         startTime: startTime,
    //         layerId: layer.id,
    //     });
    //     if (clip) {
    //         // let librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
    //         // if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
    //         //     librettoClipId = GenerateLibrettoClipId();
    //         //     clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
    //         // }
    //         //ApplicationStore.setSelectedLibrettoClipId(librettoClipId);
    //     }
    // };

    const handleWheel = (event) => {
        const container = timelineTracksRef.current;
        if (!container) return;

        event.preventDefault();

        if (event.ctrlKey) {
            // Interpret as zoom
            const zoomChange = -event.deltaY * ZOOM_SENSITIVITY; // Negative to match natural zoom direction
            const newZoom = clamp(TimelineStore.zoom + zoomChange, MIN_ZOOM, MAX_ZOOM);
            TimelineStore.setZoom(newZoom);
        } else {
            container.scrollLeft += event.deltaY + event.deltaX;
        }
    };

    // @ts-ignore
    const renderTrackDivider = (index) => {
        const newIndex = layersOrder.length - index;
        return (
            <TrackDivider
                index={newIndex}
                // @ts-ignore
                onDropMediaClip={(event, mediaId) => {}}
                // @ts-ignore
                onDropTextClip={(event, title) => {}}
            />
        );
    };

    useEffect(() => {
        // @ts-ignore
        const handleScrollToPage = (event) => {
            console.log("Handle scroll to page event: ", event.detail);
            const trackOffsetWidth = TimelineStore.tracksContainerWidth;
            const page = event.detail;
            if (timelineTracksRef.current) {
                // @ts-ignore
                console.log("Scroll to page: ", page, " Track offset width: ", trackOffsetWidth);
                timelineTracksRef.current.scrollLeft = page * trackOffsetWidth;
            }
        };

        window.addEventListener('scrollToPage', handleScrollToPage);
        return () => window.removeEventListener('scrollToPage', handleScrollToPage);
    }, []);

    const layersOrder = [...orderedLayerIds].reverse();
    const tracksWidth = CompositionStateStore.duration * TIMELINE_RULER_TICK_WIDTH_PX * TimelineStore.zoom;

    return (
        <div id={TIMELINE_ELEMENT_ID} className="timeline">
            <div id={TIMELINE_CONTROLS_ELEMENT_ID} className="timeline__controls">
                <Controls timelineHidden={timelineHidden} toggleTimelineHide={toggleTimelineHide} />
            </div>

            <div className="timeline__container" onWheel={handleWheel}>
                {/*{layersOrder.length === 0 && <EmptyTimelineButton/>}*/}

                <div className="timeline__canvas-ruler">
                    <CanvasTimeRuler />
                </div>

                <div className="timeline__playhead" style={{ transform: `translateX(calc(-50% + ${playheadX}px))` }}>
                    {/* @ts-ignore */}
                    <Playhead containerElement={document} />
                </div>

                {/*<div className="timeline__divider-top">{renderTrackDivider(0)}</div>*/}

                <div
                    id={TIMELINE_TRACKS_CONTAINER_ID}
                    className="timeline__tracks"
                    onMouseDown={() => {
                        //ApplicationStore.setSelectedLibrettoClipIdToNull();
                    }}
                    ref={timelineTracksRef}
                >
                    {layersOrder.length > 0 && (
                        <div className="tracks" style={{ minWidth: `${tracksWidth}px` }}>
                            {layersOrder.map((layerId, index) => (
                                // @ts-ignore
                                <Track layerId={layerId}>
                                    {index < layersOrder.length - 1 && renderTrackDivider(index + 1)}
                                </Track>
                            ))}
                        </div>
                    )}

                    <div className="timeline__void-container">{renderTrackDivider(layersOrder.length)}</div>
                </div>
            </div>
        </div>
    );
});

export default Timeline;